import React, { useCallback, useEffect, useRef, useState } from "react";

import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";

import { useAppSelector } from "store/rootReducer";

import { httpType } from "global/types";

import { ContainerCrud, permitionsCrud } from "features/cruds";
import { IFormOneLabel } from "features/forms/FormOneLabel/interface";
import { IModelFormBase } from "features/forms/optionsForms";
import { AdminAreaSkeleton } from "features/LoadSkeleton";

import api from "services/api";
import { ParticipantesService } from "services/participantes";

import { FORMS_ADMIN } from "utils/entities/permission";

export const AttendantRole: React.FC = () => {
  const { t } = useTranslation();
  const [onSubmitForm, setOnSubmitForm] = useState<number>(0);
  const [objectRetorned, setObjectRetorned] = useState<IFormOneLabel | null>(
    null,
  );
  const [httpType, setHttpType] = useState<httpType>("post");
  const [listAttendant, setListAttendant] = useState<Array<IFormOneLabel>>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [situation, setSituation] = useState<number>(2);

  const { user } = useAppSelector((state) => state.user);
  const { globalConfigurations } = useSelector(
    (state: any) => state.configurations,
  );

  const { read, create, edit } = usePermissions(FORMS_ADMIN.ATTENDANT_ROLE);
  const { addToast } = useToast();
  const fetchIdRef = useRef(0);

  useEffect(() => {
    const paginationList = Array.isArray(globalConfigurations)
      ? globalConfigurations?.find((x: any) => x?.name === "PaginationList")
      : null;
    const paginationValue = paginationList?.value
      ? parseInt(paginationList.value)
      : 10;
    setPageSize(paginationValue);
  }, [globalConfigurations]);

  useEffect(() => {
    if (pageSize > 0) {
      setCurrentPage(0);
      onLoad(0);
    }
  }, [situation, pageSize]);

  async function onLoad(page?: number, search?: string) {
    setLoading(true);
    const fetchId = ++fetchIdRef.current;

    try {
      const response = await ParticipantesService.getAllPaginated({
        pageNumber: (page ?? currentPage) + 1,
        pageSize,
        searchTerm: search ?? searchTerm,
        active: situation,
      });

      if (fetchId !== fetchIdRef.current) return;
      const data = response?.data?.data ?? response?.data;
      const items = (Array.isArray(data)
        ? data
        : data?.items ?? []) as Array<IFormOneLabel>;

      setListAttendant(items);
      setTotalCount(data?.totalCount ?? items.length);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        addToast({
          type: "error",
          title: t("common.accessDenied"),
          description: t("participantType.messages.noPermissionAccess"),
        });
      }

      console.error("Erro ao buscar os tipos de participante:", error);
      setListAttendant([]);
      setTotalCount(0);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      onLoad(0, value);
    }, 400),
    [situation, pageSize],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
    debouncedSearch(search);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    onLoad(page);
  };

  const handleSituationChange = (activeSituation: number) => {
    setSituation(activeSituation);
    setCurrentPage(0);
  };

  const serverPageCount = Math.ceil(totalCount / pageSize);

  const getCustomerIdLocalStorage = () => {
    return user?.customerId;
  };

  const modelFormBase: IModelFormBase = {
    api: api,
    endPoint: "/v1/AttendantRole/",
    httpType: httpType,
    setHttpType: setHttpType,
    list: listAttendant,
    onSubmitForm: onSubmitForm,
    setOnSubmitForm: setOnSubmitForm,
    objectRetorned: objectRetorned,
    setObjectRetorned: setObjectRetorned,
  };

  const formOneLabel: IFormOneLabel = {
    active: true,
    customerId: getCustomerIdLocalStorage(),
    displayLabel: t("participantType.title"),
    id: 0,
    name: "",
    modelFormBase: modelFormBase,
    titleToast: t("participantType.title"),
  };

  const permissions: permitionsCrud = {
    inactivate: false,
    toCreate: create,
    toEdit: edit,
    toRead: read,
  };

  return (
    <ContainerCrud
      list={listAttendant}
      title={t("participantType.title")}
      emptyListMessage={t("participantType.notFound")}
      permissions={permissions}
      customProsForm={formOneLabel}
      modelFormBase={modelFormBase}
      optionForm="FormOneLabel"
      loading={loading}
      isAudience={t("participantType.title")}
      SkeletonComponent={AdminAreaSkeleton}
      serverSidePagination
      totalCount={totalCount}
      currentPage={currentPage}
      serverPageCount={serverPageCount}
      onPageChange={handlePageChange}
      onSearchChange={handleSearchChange}
      onSituationChange={handleSituationChange}
      onAfterSubmit={() => onLoad(currentPage)}
    />
  );
};
